import { useState } from "react";
import { Link } from "react-router";
import { BadgeCheck, Briefcase, Building2, Flag, Mail, ShieldCheck, UserCheck, Users } from "lucide-react";
import { Alert, Badge, Button, Card, CardBody, PageHeader } from "@fellowflats/ui";
import { EmailTestReplySchema, OverviewSchema } from "@fellowflats/contracts/admin";
import { api } from "../lib/api";
import { describe, useApi } from "../lib/use-api";
import { Loaded } from "../components/bits";

export function Overview() {
  const state = useApi(() => api("GET", "/admin/overview", OverviewSchema));
  return (
    <div className="space-y-8">
      <PageHeader title="Overview" description="What needs a person, and the platform at a glance." />
      <Loaded state={state}>
        {(o) => {
          const stats = [
            { icon: UserCheck, label: "Landlords to review", value: o.pendingLandlords, to: "/verifications", alert: true },
            { icon: BadgeCheck, label: "Listings to verify", value: o.pendingListingVerifications, to: "/verifications", alert: true },
            { icon: Flag, label: "Open reports", value: o.openReports, to: "/reports", alert: true },
            { icon: Briefcase, label: "Open leads", value: o.openLeads, to: "/leads" },
            { icon: Building2, label: "Active listings", value: o.activeListings, to: "/analytics" },
            { icon: Users, label: "Renters looking", value: o.lookingTenants, to: "/analytics" },
            { icon: ShieldCheck, label: "Suspended accounts", value: o.suspendedUsers, to: "/people" },
          ];
          return (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {stats.map((s) => (
                  <Link key={s.label} to={s.to} className="rounded-xl border border-border bg-card p-5 transition-colors hover:border-primary">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                      <s.icon size={18} aria-hidden="true" />
                    </span>
                    <p className="mt-3 flex items-baseline gap-2 text-3xl font-bold tracking-tight">
                      {s.value}
                      {s.alert && s.value > 0 && <Badge tone="warning">action needed</Badge>}
                    </p>
                    <p className="text-sm text-muted-foreground">{s.label}</p>
                  </Link>
                ))}
              </div>
              <EmailHealth email={o.email} />
            </>
          );
        }}
      </Loaded>
    </div>
  );
}

function EmailHealth({ email }: { email: { from: string; hasApiKey: boolean; adminNotifyEmail: boolean } }) {
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Card>
      <CardBody className="space-y-3 p-5">
        <h2 className="flex items-center gap-2 font-semibold">
          <Mail size={16} aria-hidden="true" /> Email
        </h2>
        <ul className="space-y-1 text-sm">
          <li>Sending key: {email.hasApiKey ? <Badge tone="success">set</Badge> : <Badge tone="danger">missing: no email is sent</Badge>}</li>
          <li>
            Admin alert inbox:{" "}
            {email.adminNotifyEmail ? <Badge tone="success">set</Badge> : <Badge tone="warning">missing: nobody hears about landlords waiting</Badge>}
          </li>
          <li className="text-muted-foreground">From: {email.from}</li>
        </ul>
        <Button
          size="sm"
          variant="outline"
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              setResult(await api("POST", "/admin/email-test", EmailTestReplySchema));
            } catch (e) {
              setResult({ ok: false, message: describe(e) });
            } finally {
              setBusy(false);
            }
          }}
        >
          Send me a test email
        </Button>
        {result && <Alert tone={result.ok ? "success" : "error"}>{result.message}</Alert>}
      </CardBody>
    </Card>
  );
}
